import React, { useEffect, useState } from 'react';
import { authService } from './auth';
import { Dashboard } from './dashboard/Dashboard';

/**
 * Root shell for the SmartWater pipeline status dashboard.
 *
 * On mount, completes the OAuth authorization-code exchange if the URL carries
 * a `?code=...` from the Cognito/Federate login redirect (see auth.ts). This is
 * the piece that closes the previously-open gap: without it, the edge Lambda's
 * cookie check never succeeds on a cold browser, and login loops indefinitely.
 * Once exchanged, the `code`/`state` querystring is stripped via
 * `history.replaceState` so it doesn't linger in the URL bar or get re-submitted
 * on a refresh (Cognito authorization codes are single-use).
 *
 * Dashboard is NOT rendered until this exchange (if one is in flight) has
 * resolved -- rendering it immediately, in parallel with the exchange, was a
 * real bug: Dashboard's effects fire immediately on mount and call
 * authService.getIdToken(), which returns null until the exchange persists
 * real tokens. On a fresh login redirect, this made every one of Dashboard's
 * API calls (listLeagues, getActiveRun, the live WebSocket subscription) fail
 * silently with "not signed in," and nothing re-triggers them once a token
 * later becomes available -- only a manual page reload (landing on the
 * already-persisted tokens from the completed exchange) recovered. Confirmed
 * via reading the actual mount order and token checks, not assumed.
 *
 * Once past the auth exchange, renders the real-time pipeline dashboard (see
 * dashboard/Dashboard.tsx) -- league discovery, live flow diagram, live
 * metric charts, eval scorecard, and past-run view.
 */
export function App() {
  const [authError, setAuthError] = useState<string | null>(null);
  // True only while a code exchange is actually in flight -- most page loads
  // carry no `?code=` at all (an already-authenticated user navigating or
  // refreshing), and those must render Dashboard immediately, not wait on a
  // phantom exchange that was never started.
  const [exchangingCode, setExchangingCode] = useState(() => new URLSearchParams(window.location.search).has('code'));

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code) return;

    authService
      .exchangeCodeForTokens(code)
      .catch((error: Error) => setAuthError(error.message))
      .finally(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
        setExchangingCode(false);
      });
  }, []);

  if (authError) {
    return (
      <div style={{ maxWidth: 800, margin: '0 auto', padding: 20, fontFamily: 'Arial, sans-serif', textAlign: 'center' }}>
        <p style={{ color: '#b91c1c' }}>Sign-in failed: {authError}</p>
      </div>
    );
  }

  if (exchangingCode) {
    return (
      <div style={{ maxWidth: 800, margin: '0 auto', padding: 20, fontFamily: 'Arial, sans-serif', textAlign: 'center' }}>
        <p>Signing in&hellip;</p>
      </div>
    );
  }

  return <Dashboard />;
}
